/** Fleet vulnerabilities, grouped by CVE with progressive disclosure of instances. */

import { plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useMemo, type ReactNode } from 'react';
import CvssScore from '../../components/CvssScore';
import { Link } from 'react-router-dom';
import Chip from '../../components/Chip';
import CveLink from '../../components/CveLink';
import DataTable, { type DataTableColumn } from '../../components/DataTable';
import EmptyState from '../../components/EmptyState';
import Icon from '../../components/Icon';
import { useAuth } from '../../lib/auth';
import { DASH, fmtDate, fmtDay, fmtNumber } from '../../lib/format';
import type { DtCveRow, DtCvesResponse, DtSeverity } from '../../lib/types';
import { DT_SEVERITY_ORDER } from '../../lib/types';
import { SCAN_STATE_LABELS, SEVERITY_LABELS, SEVERITY_TONE, fmtEpss, severityRank } from './severity';
import CveDetail from './CveDetail';
import './cve-panel.css';

export interface CveFilters {
  severity: string;
  onlyKev: boolean;
  search: string;
}

export interface CvePanelProps {
  data: DtCvesResponse;
  filters: CveFilters;
  onFiltersChange: (filters: CveFilters) => void;
  includeSuppressed: boolean;
  onToggleSuppressed: (value: boolean) => void;
}

function PanelIcon({ name }: { name: 'alert' | 'chevron' }): ReactNode {
  const paths = {
    alert: <><path d="m10.3 4-8 14a2 2 0 0 0 1.7 3h16a2 2 0 0 0 1.7-3l-8-14a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4m0 4h.01" /></>,
    chevron: <path d="m9 5 7 7-7 7" />,
  };
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}

function SeverityBadge({ severity }: { severity: DtSeverity }): ReactNode {
  return <Chip tone={SEVERITY_TONE[severity] ?? 'neutral'}>{SEVERITY_LABELS[severity] ?? severity}</Chip>;
}

/** Keep the overview short; versions and complete product lists live in the detail. */
function ProduktList({ row }: { row: DtCveRow }): ReactNode {
  const { t } = useLingui();
  if (row.produkte.length === 0) return <span className="muted">{DASH}</span>;
  const more = fmtNumber(row.produkte.length - 2);
  return (
    <div className="cve-product-preview">
      {row.produkte.slice(0, 2).map((produkt) => {
        const count = produkt.instanz_count;
        return (
          <span key={`${produkt.produkt}@${produkt.version}`} className="cve-product-preview-item">
            <span title={produkt.produkt}>{produkt.produkt || DASH}</span>
            <span className="cve-product-count" title={t`${plural(count, { one: '# betroffene Instanz', other: '# betroffene Instanzen' })}`}>{fmtNumber(count)}</span>
          </span>
        );
      })}
      {row.produkte.length > 2 ? <span className="cve-product-more">{t`+${more} weitere`}</span> : null}
    </div>
  );
}

export function CvePanel({ data, filters, onFiltersChange, includeSuppressed, onToggleSuppressed }: CvePanelProps): ReactNode {
  const { has } = useAuth();
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const { severity, onlyKev, search } = filters;
  const canReadCmdb = has('cmdb.read');
  const hasFilters = severity !== '' || onlyKev || includeSuppressed || search.trim() !== '';

  const resetFilters = (): void => {
    onFiltersChange({ severity: '', onlyKev: false, search: '' });
    if (includeSuppressed) onToggleSuppressed(false);
  };

  const rows = useMemo((): DtCveRow[] => {
    const term = search.trim().toLowerCase();
    return data.rows.filter((row) => {
      if (severity !== '' && row.severity !== severity) return false;
      if (onlyKev && !row.kev) return false;
      if (term === '') return true;
      return (
        row.cve_id.toLowerCase().includes(term) ||
        row.title.toLowerCase().includes(term) ||
        (row.source_label ?? row.source ?? '').toLowerCase().includes(term) ||
        row.produkte.some((p) => `${p.produkt} ${p.version}`.toLowerCase().includes(term)) ||
        row.komponenten.some((c) => c.toLowerCase().includes(term)) ||
        row.instanzen.some((inst) => `${inst.kunde} ${inst.seriennummer}`.toLowerCase().includes(term))
      );
    });
  }, [data.rows, onlyKev, search, severity]);

  const columns = useMemo((): readonly DataTableColumn<DtCveRow>[] => [
    {
      key: 'cve', header: t`Schwachstelle`, width: '22%', sortValue: (row) => row.cve_id,
      render: (row) => <div className="cve-identity">
        <div className="cve-source-links"><CveLink cve={row.cve_id} tone="neutral" source={row.source} links={row.source_links?.slice(0, 1)} /></div>
        <div className="cve-identity-meta">
          {row.kev ? <Chip tone="alert" title={t`KEV: Diese Schwachstelle wird aktiv ausgenutzt`}>KEV</Chip> : null}
          <span className="cve-source-label" title={row.source_label || row.source || undefined}>{row.source_label || row.source || <><Trans>Quelle öffnen</Trans> <Icon name="external" size={12} /></>}</span>
        </div>
      </div>,
    },
    {
      key: 'severity', header: t`Schweregrad`, width: '14%',
      headerTitle: t`Schlechtester Wert über alle betroffenen Instanzen`,
      sortValue: (row) => severityRank(row.severity), render: (row) => <SeverityBadge severity={row.severity} />,
    },
    {
      key: 'cvss', header: 'CVSS', width: '8%', align: 'right',
      headerTitle: t`Technischer Schweregrad auf einer Skala von 0 bis 10`,
      sortValue: (row) => row.cvss_score ?? -1,
      render: (row) => <CvssScore score={row.cvss_score} />,
    },
    {
      key: 'epss', header: 'EPSS', width: '8%', align: 'right',
      headerTitle: t`Wahrscheinlichkeit einer Ausnutzung in den nächsten 30 Tagen`,
      sortValue: (row) => row.epss_score ?? -1,
      render: (row) => <span className="cve-epss">{fmtEpss(row.epss_score) ?? <span className="muted">{DASH}</span>}</span>,
    },
    {
      key: 'produkte', header: t`Betroffene Produkte`, sortValue: (row) => row.produkt_count,
      cellClassName: 'cve-products-cell', render: (row) => <ProduktList row={row} />,
    },
    {
      key: 'instanzen', header: t`Instanzen`, width: '10%', align: 'center',
      headerTitle: t`Ausgelieferte Instanzen mit dieser Schwachstelle`,
      sortValue: (row) => row.instanz_count,
      render: (row) => <Chip>{fmtNumber(row.instanz_count)}</Chip>,
    },
    {
      key: 'published', header: t`Veröffentlicht`, width: '13%',
      sortValue: (row) => String(row.published ?? ''),
      render: (row) => <span className="cve-published" title={fmtDate(row.published)}>{fmtDay(row.published)}</span>,
    },
    // Headers are translated: rebuild them on a language switch.
  ], [locale]);


  const total = data.rows.length;
  const shownText = fmtNumber(rows.length);
  const totalText = fmtNumber(total);
  const countText = rows.length === total
    ? t`${plural(total, { one: '# Schwachstelle', other: '# Schwachstellen' })}`
    : t`${shownText} von ${totalText} Schwachstellen`;
  const uncheckedCount = data.nicht_geprueft.length;

  // Rendered straight into the table card (`.card.cve-panel`): band, then the
  // flush table. The coverage warning lives above the card (CoverageWarning).
  return (
    <>
      <div className="table-toolbar" role="search" aria-label={t`Schwachstellen filtern`}>
        <input type="search" value={search} placeholder={t`CVE, Produkt, Komponente oder Quelle suchen…`} aria-label={t`CVEs durchsuchen`} onChange={(event) => onFiltersChange({ ...filters, search: event.target.value })} />
        <select value={severity} aria-label={t`Nach Schwere filtern`} onChange={(event) => onFiltersChange({ ...filters, severity: event.target.value })}>
          <option value=""><Trans>Alle Schweregrade</Trans></option>{DT_SEVERITY_ORDER.map((value) => <option key={value} value={value}>{SEVERITY_LABELS[value]}</option>)}
        </select>
        <div className="table-toolbar-options">
          <label className="check" title={t`Nur bekannte, aktiv ausgenutzte Schwachstellen (KEV) anzeigen`}>
            <input type="checkbox" checked={onlyKev} onChange={(event) => onFiltersChange({ ...filters, onlyKev: event.target.checked })} />
            <Trans>Nur aktiv ausgenutzt</Trans>
          </label>
          <label className="check" title={t`In Dependency-Track unterdrückte Befunde einschließen. Unterdrückte Komponenten werden in den Details gekennzeichnet.`}>
            <input type="checkbox" checked={includeSuppressed} onChange={(event) => onToggleSuppressed(event.target.checked)} />
            <Trans>Unterdrückte einbeziehen</Trans>
          </label>
          {hasFilters ? <button type="button" className="link" onClick={resetFilters}><Trans>Filter zurücksetzen</Trans></button> : null}
          <span className="table-count" aria-live="polite">{countText}</span>
        </div>
      </div>

      {rows.length === 0 ? (
        total > 0 ? (
          <EmptyState title={t`Keine passenden Schwachstellen`}><Trans>Passen Sie den Suchbegriff oder die Filter an.</Trans></EmptyState>
        ) : data.nicht_geprueft.length > 0 ? (
          // Instances that could not be checked may well be affected: no all-clear.
          <EmptyState title={t`Kein vollständiges Ergebnis`}>
            {t`${plural(uncheckedCount, {
              one: 'Für die geprüften Instanzen meldet Dependency-Track keine Schwachstellen, # Instanz ist jedoch nicht prüfbar (siehe Hinweis oben).',
              other: 'Für die geprüften Instanzen meldet Dependency-Track keine Schwachstellen, # Instanzen sind jedoch nicht prüfbar (siehe Hinweis oben).',
            })}`}
          </EmptyState>
        ) : (
          <EmptyState title={t`Keine Schwachstellen gemeldet`}><Trans>Dependency-Track meldet für die geprüften Instanzen keine Schwachstellen.</Trans></EmptyState>
        )
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(row) => row.cve_id}
          renderDetail={(row) => <CveDetail row={row} canReadCmdb={canReadCmdb} />}
          mobileBreakpoint={960}
          pageSize={15}
          resetPageOnRowsChange={false}
          paginationResetKey={`${severity}|${onlyKev}|${search}|${includeSuppressed}`}
          caption={t`Schwachstellen nach CVE mit betroffenen Produkten und Instanzen`}
        />
      )}
    </>
  );
}

/** Instances without scan data: shown above the CVE card, the list stays expandable. */
export function CoverageWarning({ data }: { data: DtCvesResponse }): ReactNode {
  const { has } = useAuth();
  const { t } = useLingui();
  const canReadCmdb = has('cmdb.read');
  const uncheckedCount = data.nicht_geprueft.length;
  if (uncheckedCount === 0) return null;
  return (
    <details className="cve-coverage-warning">
      <summary>
        <span className="cve-warning-icon"><PanelIcon name="alert" /></span>
        <span className="cve-warning-copy"><strong>{t`${plural(uncheckedCount, { one: '# Instanz noch nicht geprüft', other: '# Instanzen noch nicht geprüft' })}`}</strong><span><Trans>Ohne Scan-Daten ist der Sicherheitsstatus unbekannt.</Trans></span></span>
        <span className="cve-warning-action"><Trans>Instanzen anzeigen</Trans><PanelIcon name="chevron" /></span>
      </summary>
      <div className="cve-coverage-detail">
        <ul>{data.nicht_geprueft.map((inst) => <li key={inst.instanz_id}>
          <div>{canReadCmdb ? <Link to={`/stammdaten?klasse=ProduktInstanz&id=${inst.instanz_id}`}>{inst.label} <Icon name="external" size={12} /></Link> : <strong>{inst.label}</strong>}<Chip tone="warn">{SCAN_STATE_LABELS[inst.scan_state] ?? inst.scan_state}</Chip></div>
          {inst.note ? <p>{inst.note}</p> : null}
        </li>)}</ul>
      </div>
    </details>
  );
}

export default CvePanel;
